import {Progressive} from '@backstage/api-types';
import {
  createComponentAdmin,
  PublishesTo,
  SubscribesTo,
  type SchemaTypeHelper,
  opacitySchema,
  StringEnum,
  SingleUnion,
} from '@backstage-components/base';
import {Type, type Static} from '@sinclair/typebox';

export const reactName = 'BackgroundVideo';
const name = 'Background Video';
export const description = reactName;

const overlayTypeEnum = ['Flat', 'Vignette', 'None'] as const;
export const animationTypeEnum = ['Fade', 'Fade and Scale', 'None'] as const;

const schema = Type.Object({
  autoplay: Type.Optional(
    Type.Boolean({
      title: 'Autoplay',
      description:
        "Start the video playback automatically, as per your device and browser's auto-play policy.",
    })
  ),
  muted: Type.Optional(
    Type.Boolean({
      title: 'Muted',
      description: 'Start the video muted.',
    })
  ),
  loop: Type.Optional(
    Type.Boolean({
      title: 'Loop',
      description: 'Continuously loop the background video.',
    })
  ),
  showPosterImage: Type.Optional(
    Type.Boolean({
      title: 'Show Poster Image',
      description: 'Show the poster image when the video is not playing.',
    })
  ),
  overlayType: StringEnum(overlayTypeEnum, {
    title: 'Overlay Type',
    default: 'None',
  }),
  animationType: StringEnum(animationTypeEnum, {
    title: 'Load Animation',
    default: 'None',
  }),
  animationDuration: Type.Optional(
    Type.Integer({
      title: 'Animation Duration',
      description: 'The duration of the animation in milliseconds.',
      default: 1000,
    })
  ),
  overlayOpacity: Type.Optional(opacitySchema),
  source: SingleUnion(Progressive),
  posterImage: Type.String({title: 'Poster Image'}),
});

const uiSchema = {
  'ui:groups': {
    'ui:template': 'tabs',
    sections: [
      [
        'Properties',
        [
          {
            'ui:template': 'accordion',
            sections: [
              ['Video', ['source', 'posterImage', 'showPosterImage']],
              ['Settings', ['autoplay', 'muted', 'loop']],
              ['Overlay', ['overlayType', 'overlayOpacity']],
              ['Animations', ['animationType', 'animationDuration']],
            ],
          },
        ],
      ],
      [
        'Styling',
        [
          {
            'ui:template': 'accordion',
            sections: [['Custom Styles', ['styleAttr']]],
          },
        ],
      ],
      [
        'Animations',
        [
          {
            'ui:template': 'accordion',
            sections: [['Animations', ['animationStates']]],
          },
        ],
      ],
    ],
  },
  overlayOpacity: {
    opacity: {
      'ui:widget': 'range',
      'ui:hideLabel': true,
      'ui:options': {
        type: 'percentage',
      },
    },
  },
  posterImage: {
    'ui:widget': 'AssetSelectWidget',
    'ui:options': {
      buttonTitle: 'Select Poster Image',
    },
  },
  source: {
    'ui:field': 'VideoField',
    // hide label here, the video field used in rjsf creates its own for content management
    'ui:label': false,
    'ui:fieldReplacesAnyOrOneOf': true,
    'ui:options': {
      videoTypesOption: ['video'],
    },
    assetId: {
      'ui:widget': 'hidden',
    },
  },
};

export const defaultFieldData: Static<typeof schema> = {
  autoplay: true,
  muted: true,
  loop: true,
  overlayType: 'Flat',
  animationType: 'Fade',
  animationDuration: 1000,
  overlayOpacity: {
    opacity: 0.5,
  },
  source: {
    progressive:
      'https://assets.lcdbackstage.com/8d3fec3d-7d4f-41a2-9661-bcb3ac716668/d3ae96cf-b534-48d7-a514-58f17dfbbaa0.mp4',
  },
  showPosterImage: true,
  posterImage:
    'https://assets.lcdbackstage.com/7c4e4973-7025-4582-992b-3e47056cabaa/addacd24-c3a1-411c-8f3d-f01de9d4e154.jpg',
};

const instructions = Type.Union([
  PublishesTo({
    topic: `${reactName}:on-play`,
    description: 'Video playback has started',
  }),
  PublishesTo({
    topic: `${reactName}:on-pause`,
    description: 'Video playback has paused',
  }),
  PublishesTo({
    topic: `${reactName}:on-ended`,
    description: 'Video playback has ended',
  }),
  SubscribesTo({
    topic: `${reactName}:play`,
    description: 'Play the current video',
  }),
  SubscribesTo({
    topic: `${reactName}:pause`,
    description: 'Pause the current video',
  }),
]);

export const ComponentDefinition = createComponentAdmin({
  id: 'd4216447-39b3-4f21-b3b8-d037a16c74a7',
  reactName,
  name,
  category: 'layout',
  description,
  defaultFieldData,
  instructions,
  schema,
  slotConfiguration: {
    items: {maxModules: 100, displayWidth: 12, title: 'Modules'},
  },
  slug: reactName,
  uiSchema,
  version: 1,
})
  .withAnimationStates()
  .withStyles()
  .build();

export type SchemaType = SchemaTypeHelper<typeof ComponentDefinition>;
